import {
  Link,
  chakra,
  PopoverTrigger,
  Portal,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  useColorModeValue,
  DarkMode,
  ListItem,
  OrderedList,
  Box,
} from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { FormFields } from '../types';
import type { SmartContractVerificationMethod, SmartContractVerificationConfig } from 'types/client/contract';

import useIsMobile from 'lib/hooks/useIsMobile';
import Popover from 'ui/shared/chakra/Popover';
import FormFieldFancySelect from 'ui/shared/forms/fields/FormFieldFancySelect';
import IconSvg from 'ui/shared/IconSvg';

import { METHOD_LABELS } from '../utils';

interface Props {
  methods: SmartContractVerificationConfig['verification_options'];
}

const ContractVerificationFieldMethod = ({ methods }: Props) => {
  const { t } = useTranslation();
  const tooltipBg = useColorModeValue('gray.700', 'gray.900');
  const isMobile = useIsMobile();

  const options = React.useMemo(() => methods.map((method) => ({
    value: method,
    label: METHOD_LABELS[method],
  })), [ methods ]);

  const renderPopoverListItem = React.useCallback((method: SmartContractVerificationMethod) => {
    switch (method) {
      case 'flattened-code':
        return <ListItem key={ method }>{ t('verification_single_file') }</ListItem>;
      case 'multi-part':
        return <ListItem key={ method }>{ t('verification_multi_part_file') }</ListItem>;
      case 'sourcify':
        return <ListItem key={ method }>{ t('verification_through') } <Link href="https://sourcify.dev/" target="_blank">{ t('Sourcify') }</Link>.</ListItem>;
      case 'standard-input':
        return (
          <ListItem key={ method }>
            <span>{ t('verification_using') } </span>
            <Link
              href="https://docs.soliditylang.org/en/latest/using-the-compiler.html#input-description"
              target="_blank"
            >
              { t('standard_input_json') }
            </Link>
            <span> { t('file') }</span>
          </ListItem>
        );
      case 'vyper-code':
        return <ListItem key={ method }>{ t('verification_vyper_contract') }</ListItem>;
      case 'vyper-multi-part':
        return <ListItem key={ method }>{ t('verification_multi_part_vyper_files') }</ListItem>;
      case 'vyper-standard-input':
        return (
          <ListItem key={ method }>
            <span>{ t('verification_vyper_using') } </span>
            <Link
              href="https://docs.vyperlang.org/en/stable/compiling-a-contract.html#compiler-input-and-output-json-description"
              target="_blank"
            >
              { t('standard_input_json') }
            </Link>
            <span> { t('file') }</span>
          </ListItem>
        );
      case 'solidity-hardhat':
        return <ListItem key={ method }>{ t('verification_through_Hardhat') }</ListItem>;
      case 'solidity-foundry':
        return <ListItem key={ method }>{ t('verification_through_Foundry') }</ListItem>;
      case 'stylus-github-repository':
        return <ListItem key={ method }>{ t('verification_contract_via_github') }</ListItem>;
    }
  }, [ t ]);

  return (
    <>
      <Box mt={{ base: 10, lg: 6 }} gridColumn={{ lg: '1 / 3' }}>
        <chakra.span fontWeight={ 500 } fontSize="lg" fontFamily="heading">
          { t('blockscout_supports_contract_methods_length', { length: methods.length }) }
        </chakra.span>
        <Popover trigger="hover" isLazy placement={ isMobile ? 'bottom-end' : 'right-start' } offset={ [ -8, 8 ] }>
          <PopoverTrigger>
            <chakra.span display="inline-block" ml={ 1 } cursor="pointer" verticalAlign="middle" h="22px">
              <IconSvg name="info" boxSize={ 5 } color="icon_info" _hover={{ color: 'link_hovered' }}/>
            </chakra.span>
          </PopoverTrigger>
          <Portal>
            <PopoverContent bgColor={ tooltipBg } w={{ base: '300px', lg: '380px' }}>
              <PopoverArrow bgColor={ tooltipBg }/>
              <PopoverBody color="white">
                <DarkMode>
                  <span>{ t('blockscout_supports_contract_methods', { length: methods.length }) }</span>
                  <OrderedList>
                    { methods.map(renderPopoverListItem) }
                  </OrderedList>
                </DarkMode>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
      </Box>
      <FormFieldFancySelect<FormFields, 'method'>
        name="method"
        placeholder={ t('verification_method_placeholder') }
        options={ options }
        isRequired
        isAsync={ false }
        isReadOnly={ options.length === 1 }
      />
    </>
  );
};

export default React.memo(ContractVerificationFieldMethod);
